import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './SignIn.css';
import logo from './logo.png';
import teacherImage from './teacher-auth-image.jpg';
import studentImage from './student-auth-image.jpg';
import { supabase } from './supabaseClient';
import { getApiBaseUrl } from './config/apiConfig';
import bcrypt from 'bcryptjs';
import { getCrossDomainUrl, isTeacherSubdomain } from './utils/subdomainDetector';
import logger from './utils/logger';

// Translations
const translations = {
  EN: {
    title: 'Welcome back',
    subtitle: 'Sign in to continue to your dashboard',
    subtitleTeacher: 'Sign in as a teacher to continue to your dashboard',
    subtitleStudent: 'Sign in as a student to continue to your dashboard',
    teacher: 'Teacher',
    student: 'Student',
    admin: 'Admin',
    email: 'Email Address',
    emailPlaceholder: 'Enter your email',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot Password?',
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    noAccount: "Don't have an account?",
    signUp: 'Sign up',
    sendResetEmail: 'Send Reset Email',
    backToSignIn: '← Back to Sign In',
    enterBothFields: 'Please enter both email and password.',
    invalidCredentials: 'Invalid email or password.',
    signInSuccess: 'Sign in successful!',
    signInFailed: 'Sign in failed.',
    tooManyAttempts: 'Too many login attempts. Please wait 15 minutes and try again.',
    adminInactive: 'Admin account is inactive. Please contact support.',
    enterEmail: 'Please enter your email.',
    invalidEmail: 'Please enter a valid email address.',
    tooManyResetAttempts: 'Too many attempts. Please try again later.',
    resetEmailSent: 'If an account exists with this email, a password reset link has been sent. Check your inbox.',
    resetFailed: 'Failed to send reset email. Please try again.',
    teacherSubdomainOnly: 'Teacher Sign In Only',
  },
  AR: {
    title: 'مرحباً بعودتك',
    subtitle: 'سجل الدخول للمتابعة إلى لوحة التحكم',
    subtitleTeacher: 'سجل الدخول كمعلم للمتابعة إلى لوحة التحكم',
    subtitleStudent: 'سجل الدخول كطالب للمتابعة إلى لوحة التحكم',
    teacher: 'معلم',
    student: 'طالب',
    admin: 'مدير',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'أدخل بريدك الإلكتروني',
    password: 'كلمة المرور',
    passwordPlaceholder: 'أدخل كلمة المرور',
    rememberMe: 'تذكرني',
    forgotPassword: 'نسيت كلمة المرور؟',
    signIn: 'تسجيل الدخول',
    signingIn: 'جاري تسجيل الدخول...',
    noAccount: 'ليس لديك حساب؟',
    signUp: 'إنشاء حساب',
    sendResetEmail: 'إرسال رابط إعادة التعيين',
    backToSignIn: '→ العودة لتسجيل الدخول',
    enterBothFields: 'يرجى إدخال البريد الإلكتروني وكلمة المرور.',
    invalidCredentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
    signInSuccess: 'تم تسجيل الدخول بنجاح!',
    signInFailed: 'فشل تسجيل الدخول.',
    tooManyAttempts: 'محاولات كثيرة جداً. يرجى الانتظار 15 دقيقة والمحاولة مرة أخرى.',
    adminInactive: 'حساب المدير غير نشط. يرجى الاتصال بالدعم.',
    enterEmail: 'يرجى إدخال بريدك الإلكتروني.',
    invalidEmail: 'يرجى إدخال بريد إلكتروني صالح.',
    tooManyResetAttempts: 'محاولات كثيرة جداً. يرجى المحاولة لاحقاً.',
    resetEmailSent: 'إذا كان هناك حساب بهذا البريد، فقد تم إرسال رابط إعادة تعيين كلمة المرور. تحقق من بريدك.',
    resetFailed: 'فشل إرسال بريد إعادة التعيين. يرجى المحاولة مرة أخرى.',
    teacherSubdomainOnly: 'تسجيل دخول المعلمين فقط',
  }
};

export default function SignIn() {
  // Automatically determine role based on subdomain
  const onTeacherSubdomain = isTeacherSubdomain();
  const searchParams = new URLSearchParams(window.location.search);
  const showAdminOption = searchParams.get('admin') === 'true';
  const returnTo = searchParams.get('returnTo'); // Get return URL for redirects after sign-in
  const isOAuthCallback = searchParams.get('oauth_callback') === 'true';

  // Set role based on subdomain or admin parameter
  const [role, setRole] = useState(
    showAdminOption ? 'admin' : onTeacherSubdomain ? 'teacher' : 'student'
  );

  const [showPassword, setShowPassword] = useState(false);
  // Get language from localStorage to stay consistent with App.js
  const [language, setLanguage] = useState(() => {
    const savedLang = localStorage.getItem('taealam_language');
    return savedLang === 'ar' ? 'AR' : 'EN';
  });
  const [form, setForm] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState('');
  const navigate = useNavigate();


  const toggleLanguage = () => {
    const newLang = language === 'EN' ? 'AR' : 'EN';
    setLanguage(newLang);
    // Save to localStorage to sync with App.js
    localStorage.setItem('taealam_language', newLang === 'AR' ? 'ar' : 'en');
    // Update document direction
    document.documentElement.dir = newLang === 'AR' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang === 'AR' ? 'ar' : 'en';
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.email || !form.password) {
      setError('Please enter both email and password.');
      return;
    }
    setLoading(true);
    try {
      // SUBDOMAIN VALIDATION - Prevent students from signing in on teacher subdomain
      if (onTeacherSubdomain && role === 'student') {
        setError('Student accounts cannot sign in on the teacher subdomain. Please visit the main domain.');
        setLoading(false);
        return;
      }

      // SECURITY: Check rate limit before attempting login
      try {
        const backendUrl = getApiBaseUrl();
        const rateLimitCheck = await fetch(`${backendUrl}/api/auth/login-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: form.email })
        });

        if (rateLimitCheck.status === 429) {
          const data = await rateLimitCheck.json();
          throw new Error(data.error || 'Too many login attempts. Please wait 15 minutes and try again.');
        }
      } catch (rateLimitError) {
        if (rateLimitError.message.includes('Too many')) {
          throw rateLimitError;
        }
        logger.warn('Rate limit check failed:', rateLimitError);
      }

      // Use Supabase Auth for sign in
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: form.email,
        password: form.password,
      });
      
      if (authError) {
        throw new Error(authError.message || 'Invalid email or password.');
      }
      
      if (!data.user) {
        throw new Error('Invalid email or password.');
      }

      // Role-based table check (skip for students to avoid RLS blocking normal logins)
      let userData = null;
      if (role !== 'student') {
        let tableName;
        if (role === 'teacher') {
          tableName = 'Teachers';
        } else if (role === 'admin') {
          tableName = 'admins';
        }

        let selectFields = 'id';
        if (role === 'teacher') {
          selectFields = 'id, confirmation_completed';
        } else if (role === 'admin') {
          selectFields = 'id, is_active';
        }

        const { data: tableUser, error: tableError } = await supabase
          .from(tableName)
          .select(selectFields)
          .eq('email', form.email)
          .single();

        if (tableError || !tableUser) {
          throw new Error('Invalid email or password');
        }

        userData = tableUser;
      }

      // Check if admin account is active
      if (role === 'admin' && userData.is_active === false) {
        throw new Error('Admin account is inactive. Please contact support.');
      }

      setSuccess('Sign in successful!');
      localStorage.setItem('userEmail', form.email);
      localStorage.setItem('userRole', role);

      // Redirect to the correct dashboard
      if (role === 'admin') {
        localStorage.setItem('userRole', 'admin');
        navigate('/dashboard/admin/overview', { replace: true });
      } else if (role === 'teacher') {
        localStorage.setItem('userRole', 'teacher');
        localStorage.setItem('userEmail', data.user.email);

        // Check if teacher has completed wizard (has approved submission)
        const { data: submissionData } = await supabase
          .from('teacher_onboarding_submissions')
          .select('status')
          .eq('teacher_id', data.user.id)
          .eq('status', 'approved')
          .maybeSingle();

        // If no approved submission, redirect to wizard
        if (!submissionData) {
          logger.debug('[DEBUG] No approved wizard submission, redirecting to wizard');
          if (!isTeacherSubdomain()) {
            window.location.href = getCrossDomainUrl('teacher', '/teacher/signup');
          } else {
            navigate('/teacher/signup', { replace: true });
          }
          return;
        }

        // Check if teacher has completed confirmation form and profile
        const { data: teacherData, error: teacherError } = await supabase
          .from('Teachers')
          .select('confirmation_completed, profile_photo, bio, subjects, grade_levels, languages, hourly_rate')
          .eq('email', data.user.email)
          .single();

        if (teacherError || !teacherData) {
          // Error fetching data or no teacher record - redirect to confirmation form
          logger.debug('[DEBUG] Teacher record not found or error, redirecting to confirmation form');
          // Check if we're on the correct subdomain
          if (!isTeacherSubdomain()) {
            window.location.href = getCrossDomainUrl('teacher', '/teacher-confirm');
          } else {
            navigate('/teacher-confirm', { replace: true });
          }
          return;
        }

        // Check if confirmation form is completed
        if (teacherData.confirmation_completed !== true) {
          // Confirmation not completed - redirect to confirmation form
          logger.debug('[DEBUG] Confirmation not completed, redirecting to confirmation form');
          // Check if we're on the correct subdomain
          if (!isTeacherSubdomain()) {
            window.location.href = getCrossDomainUrl('teacher', '/teacher-confirm');
          } else {
            navigate('/teacher-confirm', { replace: true });
          }
          return;
        }
        
        const isProfileComplete = !!(
          teacherData?.profile_photo &&
          teacherData?.bio &&
          teacherData?.bio.trim().length > 0 &&
          teacherData?.subjects &&
          Array.isArray(teacherData.subjects) &&
          teacherData.subjects.length > 0 &&
          teacherData?.grade_levels &&
          Array.isArray(teacherData.grade_levels) &&
          teacherData.grade_levels.length > 0 &&
          teacherData?.languages &&
          Array.isArray(teacherData.languages) &&
          teacherData.languages.length > 0 &&
          teacherData?.hourly_rate &&
          teacherData.hourly_rate > 0
        );

        if (isProfileComplete) {
          // Both confirmation and profile completed - go to dashboard
          logger.debug('[DEBUG] Confirmation and profile completed, redirecting to dashboard');
          // Check if we're on the correct subdomain
          if (!isTeacherSubdomain()) {
            window.location.href = getCrossDomainUrl('teacher', '/dashboard/teacher/dashboard');
          } else {
            navigate('/dashboard/teacher/dashboard', { replace: true });
          }
        } else {
          // Profile not complete - redirect to profile page
          logger.debug('[DEBUG] Profile not complete, redirecting to profile page');
          // Check if we're on the correct subdomain
          if (!isTeacherSubdomain()) {
            window.location.href = getCrossDomainUrl('teacher', '/dashboard/teacher/profile');
          } else {
            navigate('/dashboard/teacher/profile', { replace: true });
          }
        }
      } else {
        localStorage.setItem('userRole', 'student');

        // If there's a returnTo URL, redirect there immediately (for rating links, etc.)
        if (returnTo) {
          logger.debug('[DEBUG] Redirecting to return URL:', returnTo);
          navigate(decodeURIComponent(returnTo), { replace: true });
          return;
        }

        // Check if student completed onboarding
        const { data: studentData, error: studentError } = await supabase
          .from('students')
          .select('onboarding_completed')
          .eq('uuid_id', data.user.id)
          .single();

        if (!studentError && studentData && studentData.onboarding_completed !== true) {
          logger.debug('[DEBUG] Onboarding not completed, redirecting to onboarding');
          // Check if we're on the teacher subdomain (shouldn't be for students)
          if (isTeacherSubdomain()) {
            window.location.href = getCrossDomainUrl('student', '/dashboard/student/onboarding');
          } else {
            navigate('/dashboard/student/onboarding', { replace: true });
          }
        } else {
          // Onboarding completed or error - go to dashboard
          logger.debug('[DEBUG] Onboarding completed, redirecting to dashboard');
          // Check if we're on the teacher subdomain (shouldn't be for students)
          if (isTeacherSubdomain()) {
            window.location.href = getCrossDomainUrl('student', '/dashboard/student/dashboard');
          } else {
            navigate('/dashboard/student/dashboard', { replace: true });
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Sign in failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetStatus('');

    if (!resetEmail) {
      setResetStatus('Please enter your email.');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(resetEmail)) {
      setResetStatus('Please enter a valid email address.');
      return;
    }

    try {
      const backendUrl = getApiBaseUrl();

      const statusResponse = await fetch(
        `${backendUrl}/api/password-reset-status/${encodeURIComponent(resetEmail)}`
      );
      const statusData = await statusResponse.json();

      if (statusData.success && !statusData.allowed) {
        setResetStatus(`Too many attempts. Please try again in ${statusData.timeRemaining || '1 hour'}.`);
        return;
      }

      const backendResponse = await fetch(
        `${backendUrl}/api/request-password-reset`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: resetEmail })
        }
      );

      const backendData = await backendResponse.json();

      if (backendResponse.status === 429) {
        setResetStatus(backendData.error || 'Too many attempts. Please try again later.');
        return;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        setResetStatus('Failed to send reset email. Please try again.');
      } else {
        setResetStatus('If an account exists with this email, a password reset link has been sent. Check your inbox.');
      }
    } catch (err) {
      setResetStatus('Failed to send reset email. Please try again.');
    }
  };

  const t = translations[language];
  const isRTL = language === 'AR';

  return (
    <div className={`auth-container ${isRTL ? 'rtl' : ''}`}>
      {/* Left Side - Form */}
      <div className="auth-form-side">
        <div className="auth-form-wrapper signin-wrapper">
          {/* Header with Logo and Language Toggle */}
          <div className="auth-header">
            <img
              src={logo}
              alt="Taealam Logo"
              className="auth-logo"
              onClick={() => navigate('/')}
            />
            <button
              className="language-toggle"
              onClick={toggleLanguage}
              aria-label="Toggle language"
            >
              {language === 'EN' ? 'AR' : 'EN'}
            </button>
          </div>

          {/* Title */}
          <h1 className="auth-title">{t.title}</h1>
          <p className="auth-subtitle">
            {onTeacherSubdomain && !showAdminOption ? t.subtitleTeacher : (!showAdminOption ? t.subtitleStudent : t.subtitle)}
          </p>

          {/* Admin Role Toggle - Only show for admin login */}
          {showAdminOption && (
            <div className="auth-toggle-row three-cols">
              <button
                className={`auth-toggle-btn ${role === 'teacher' ? 'active' : ''}`}
                onClick={() => setRole('teacher')}
              >
                {t.teacher}
              </button>
              <button
                className={`auth-toggle-btn ${role === 'student' ? 'active' : ''}`}
                onClick={() => setRole('student')}
              >
                {t.student}
              </button>
              <button
                className={`auth-toggle-btn ${role === 'admin' ? 'active' : ''}`}
                onClick={() => setRole('admin')}
              >
                {t.admin}
              </button>
            </div>
          )}

          {!showReset ? (
            <form className="auth-form" onSubmit={handleSubmit}>
              {/* Email */}
              <div className="form-group">
                <label>{t.email}</label>
                <input 
                  type="email" 
                  name="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder={t.emailPlaceholder} 
                  value={form.email} 
                  onChange={handleChange} 
                />
              </div>

              {/* Password */}
              <div className="form-group">
                <label>{t.password}</label>
                <div className="password-input-wrapper">
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    name="password" 
                    placeholder={t.passwordPlaceholder} 
                    value={form.password} 
                    onChange={handleChange} 
                  />
                  <button 
                    type="button" 
                    className="password-toggle" 
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* Remember me & Forgot */}
              <div className="signin-options">
                <label className="checkbox-label">
                  <input type="checkbox" name="remember" />
                  <span className="checkbox-custom"></span>
                  {t.rememberMe}
                </label>
                <button 
                  type="button" 
                  className="forgot-password-btn"
                  onClick={() => setShowReset(true)}
                >
                  {t.forgotPassword}
                </button>
              </div>

              {/* Error/Success Messages */}
              {error && <div className="auth-error">{error}</div>}
              {success && <div className="auth-success">{success}</div>}

              {/* Submit Button */}
              <button 
                type="submit" 
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? t.signingIn : t.signIn}
              </button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={handleResetPassword}>
              <div className="form-group">
                <label>{t.email}</label>
                <input 
                  type="email" 
                  name="resetEmail"
                  placeholder={t.emailPlaceholder} 
                  value={resetEmail} 
                  onChange={e => setResetEmail(e.target.value)} 
                />
              </div>

              {resetStatus && (
                <div className={resetStatus.includes('sent') || resetStatus.includes('تم') ? 'auth-success' : 'auth-error'}>
                  {resetStatus}
                </div>
              )}

              <button type="submit" className="auth-submit-btn">
                {t.sendResetEmail}
              </button>

              <button 
                type="button" 
                className="back-to-signin-btn"
                onClick={() => setShowReset(false)}
              >
                {t.backToSignIn}
              </button>
            </form>
          )}

          {/* Sign Up Link */}
          <p className="auth-switch-link">
            {t.noAccount} <a href="/signup">{t.signUp}</a>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="auth-image-side">
        <img 
          src={role === 'teacher' ? teacherImage : studentImage} 
          alt={role === 'teacher' ? 'Teacher Dashboard' : 'Student Learning'} 
          className="auth-hero-image" 
        />
        <div className="auth-image-overlay"></div>
      </div>
    </div>
  );
}

// Icons
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);

